/* eslint-disable no-unused-vars */

export enum MessageType {
  USER = 'user',
  AI = 'ai',
  QUICK_SUGGESTION = 'quick_suggestion',
  PROGRESS = 'progress',
}

export type ChatMessage = {
  id: string;
  type: MessageType;
  content: string;
  // createdAt: number;
  status?: 'sending' | 'streaming' | 'sent' | 'error';
};

// ===== API Response Types =====

export enum StreamResponseType {
  PROGRESS = 'progress',
  PROGRESS_CLEAR = 'progress_clear',
  STREAM = 'stream',
  PROPERTY_METADATA = 'property_metadata',
  POI_PINS = 'poi_pins',
  TRANSPORT_ROUTES = 'transport_routes',
  TRANSPORT_STOPS = 'transport_stops',
  DLOI_PINS = 'dloi_pins',
  COMMUTE_ROUTES = 'commute_routes',
  EXTRACTED_FILTERS = 'extracted_filters',
  CLEAR_ALL = 'clear_all',
  CLEAR_POI = 'clear_poi',
  CLEAR_TRANSIT = 'clear_transit',
  CLEAR_LISTINGS = 'clear_listings',
  DONE = 'done',
  ERROR = 'error',
  LOADING_EVENT = 'loading_event',
}

export type ProgressMessage = {
  type: 'progress';
  content: string;
};

export type ProgressClearMessage = {
  type: 'progress_clear';
};

export type StreamMessage = {
  type: 'stream';
  content: string;
};

export type Property = {
  id?: string;
  property_id?: string;
  property_name?: string;
  title?: string;
  address_primary?: string;
  address?: string;
  price?: number;
  cost?: number;
  bedrooms?: number;
  bathrooms?: number;
  latitude?: number;
  longitude?: number;
  images?: string[];
  distance_from_search_center_km?: number;
  distance_from_search_center_miles?: number;
  best_commute_minutes?: number;
  best_commute_mode?: string;
  commute_details?: Array<{
    mode: string;
    estimated_minutes: number;
    target_text?: string;
  }>;
  agent_name?: string;
  unit_code?: string;
  date_available?: string;
  pet_friendly?: boolean;
  security_deposit?: number | null;
  broker_fee?: number | null;
  last_update?: string;
  creation_date?: string;
};

export type PropertyMetadata = {
  type: 'property_metadata';
  properties: Property[];
  pagination?: {
    current_page: number;
    total_properties: number;
    total_pages: number;
    has_more: boolean;
  };
};

export type LoadingEvent = {
  type: 'loading_event';
  needs_search: boolean;
  is_out_of_scope: boolean;
  is_property_detail?: boolean;
};

export type POI = {
  name: string;
  category: string;
  address: string;
  rating?: number;
  coordinates: [number, number];
};

export type POIPin = {
  poi: POI;
  distance_from_center: number;
};

export type POIPins = {
  type: 'poi_pins';
  pins: POIPin[];
};

export type TransportRoute = {
  station_name: string;
  route_name?: string;
  route_short_name?: string;
  route_long_name?: string;
  transport_type: string;
  latitude: number;
  longitude: number;
  distance_km: number;
  hex_color?: string;
};

export type TransportRoutes = {
  type: 'transport_routes';
  routes: TransportRoute[];
};

export type TransportStop = {
  station_name: string;
  latitude: number;
  longitude: number;
  distance_km: number;
  routes: TransportRoute[];
};

export type TransportStops = {
  type: 'transport_stops';
  stops: TransportStop[];
};

export type DLOIPin = {
  name: string;
  latitude: number;
  longitude: number;
  place_id?: string;
  image_url?: string;
  icon_url?: string;
  google_maps_url?: string;
};

export type DLOIPins = {
  type: 'dloi_pins';
  pins: DLOIPin[];
};

export type CommuteRouteStep = {
  mode: 'walk' | 'transit';
  minutes: number;
  vehicle_type?: 'bus' | 'subway' | 'train' | 'tram' | 'ferry' | 'rail';
  line_name?: string;
  line_short_name?: string;
  line_color?: string;
  headsign?: string;
  num_stops?: number;
  departure_stop?: string;
  arrival_stop?: string;
  departure_stop_lat?: number;
  departure_stop_lng?: number;
  arrival_stop_lat?: number;
  arrival_stop_lng?: number;
  polyline?: string;
};

export type CommuteRoute = {
  polyline?: string;
  stroke_color?: string;
  mode?: string;
  mode_sequence?: string[];
  minutes?: number;
  steps?: CommuteRouteStep[];
  target_text?: string;
};

export type CommutRoutes = {
  type: 'commute_routes';
  routes: CommuteRoute[];
};

export type ExtractedFilters = {
  type: 'extracted_filters';
  filters: ExtractedFiltersData;
};

export type DoneMessage = {
  type: 'done';
};

export type ErrorMessage = {
  type: 'error';
  content: string;
};

export interface ExtractedFiltersData {
  bedrooms?: {
    min: number | null;
    max: number | null;
  };
  bathrooms?: {
    min: number | null;
    max: number | null;
  };
  price?: {
    min: number | null;
    max: number | null;
  };
  amenities?: string[];
  not_amenities?: string[];
  size?: {
    min_sqft: number | null;
    max_sqft: number | null;
  };
}

/** @deprecated use ExtractedFiltersData */
export type MetaFiltersData = ExtractedFiltersData;

export type ClearMessage =
  | { type: 'clear_all' }
  | { type: 'clear_poi' }
  | { type: 'clear_transit' }
  | { type: 'clear_listings' };

export type ChatStreamEvent =
  | ProgressMessage
  | ProgressClearMessage
  | StreamMessage
  | PropertyMetadata
  | POIPins
  | TransportRoutes
  | TransportStops
  | DLOIPins
  | CommutRoutes
  | ExtractedFilters
  | ClearMessage
  | DoneMessage
  | ErrorMessage
  | LoadingEvent;

// ===== Context Type =====

export type APIData = {
  properties: Property[];
  pois: POIPin[];
  transportRoutes: TransportRoute[];
  transportStops: TransportStop[];
  dloiPins: DLOIPin[];
  commuteRoutes: CommuteRoute[];
  metaFilters?: ExtractedFiltersData | null;
};

export type ChatContextType = {
  // UI state
  isOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;

  // Message state
  messages: ChatMessage[];
  isStreaming: boolean;
  appendMessage: (msg: ChatMessage) => void;
  updateLastAIMessage: (chunk: string, done?: boolean) => void;
  resetMessages: () => void;

  // Session & History
  sessionId: string;
  history: Array<{ role: 'user' | 'system'; content: string }>;

  // API Data
  apiData: APIData;

  // Search state
  hasAISearchCompleted: boolean;
  isPropertyDetail: boolean;

  // Actions
  sendMessage: (message: string, messageType?: MessageType) => Promise<void>;
};
